import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from '../../components/Header/Header';
import PublicListPromoBar from '../../components/PublicListPromoBar';
import { tickerApi } from '../../services/tickerApi';
import { userTickerApi } from '../../services/userTickerApi';
import TabBar from '../../components/Common/TabBar';
import { useAuth } from '../../contexts/AuthContext';
import { LockIcon } from '../../components/StockTable';
import { useFontSize } from '../../contexts/FontSizeContext';
import { getTextSizeClass, getHeaderTextSizeClass, getStatisticLabelSizeClass, getSmallTextSizeClass } from '../../utils/fontSizeUtils';
import { saveUserTickerPreferences, loadUserTickerPreferences } from '../../utils/tickerPreferencesUtils';
import { GoldenCrossIcon, DeathCrossIcon, SupportIcon, ResistanceIcon } from '../../components/Icons';
import CategoryInfoModal from '../../components/CategoryInfoModal';
import Footer from '../../components/Footer';
import {
  TECHNICALS_DATE_COLUMNS,
  createIndicatorAccessors,
  hasGoldenCross as hasGoldenCrossUtil,
  hasDeathCross as hasDeathCrossUtil,
  getTechnicalsValueColor,
  formatTechnicalsStatisticValue,
  getCrossIcon,
  type TickerTechnicalDataFromAPI,
  type DailyPriceData,
} from '../../utils/tickerTechnicalsUtils';
import { formatNumber, getBollingerBandIcon as getBollingerBandIconFromUtils, getFibonacciIcon as getFibonacciIconFromUtils } from '../../utils/technicalsTabUtils';
import { publicListApi } from '../../services/publicListApi';
import { useTickerPageMeta } from '../../hooks/useTickerPageMeta';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { calculateTickerTechnicalsStatisticsColumnWidth } from '../../utils/tickerPageColumnUtils';

// Technical statistics configuration organized by category (same as ListTechnicals)
const TECHNICAL_STATISTICS = [
  // Range Analysis
  { key: '52W_HIGH', label: '52-Week High' },
  { key: 'last_closing_price', label: 'Last Closing Price' },
  { key: '52W_LOW', label: '52-Week Low' },
  { key: 'pct_off_52w_high', label: '% Below 52-Week High' },
  { key: 'pct_above_52w_low', label: '% Above 52-Week Low' },
  
  // Simple Moving Average
  { key: 'SMA_5', label: '5-Day SMA' },
  { key: 'SMA_10', label: '10-Day SMA' },
  { key: 'SMA_20', label: '20-Day SMA' },
  { key: 'SMA_50', label: '50-Day SMA' },
  { key: 'SMA_100', label: '100-Day SMA' },
  { key: 'SMA_200', label: '200-Day SMA' },
  { key: 'SMA_W50', label: '50-Week SMA' },
  { key: 'SMA_W200', label: '200-Week SMA' },
  
  // Volume Moving Average
  { key: 'VMA_5', label: '5-Day VMA' },
  { key: 'VMA_10', label: '10-Day VMA' },
  { key: 'VMA_20', label: '20-Day VMA' },
  { key: 'VMA_50', label: '50-Day VMA' },
  { key: 'VMA_100', label: '100-Day VMA' },
  { key: 'VMA_200', label: '200-Day VMA' },
  
  // Exponential Moving Average
  { key: 'EMA_9', label: '9-Day EMA' },
  { key: 'EMA_12', label: '12-Day EMA' },
  { key: 'EMA_21', label: '21-Day EMA' },
  { key: 'EMA_26', label: '26-Day EMA' },
  { key: 'EMA_50', label: '50-Day EMA' },
  { key: 'EMA_200', label: '200-Day EMA' },
  
  // Relative Strength Index
  { key: 'RSI_9', label: '9-Day RSI' },
  { key: 'RSI_14', label: '14-Day RSI' },
  { key: 'RSI_21', label: '21-Day RSI' },
  
  // Moving Average Convergence Divergence (MACD)
  { key: 'MACD_LINE', label: 'MACD Line' },
  { key: 'MACD_SIGNAL', label: 'MACD Signal' },
  { key: 'MACD_HIST', label: 'MACD Histogram' },
  
  // Stochastic Oscillator
  { key: 'STOCH_9_K', label: '9-Day %K' },
  { key: 'STOCH_9_D', label: '9-Day %D' },
  { key: 'STOCH_14_K', label: '14-Day %K' },
  { key: 'STOCH_14_D', label: '14-Day %D' },
  { key: 'STOCH_21_K', label: '21-Day %K' },
  { key: 'STOCH_21_D', label: '21-Day %D' },
  
  // Bollinger Bands
  { key: 'BB_20_UPPER_20', label: '20-Day Upper BB' },
  { key: 'BB_20_SMA', label: '20-Day Middle BB' },
  { key: 'BB_20_LOWER_20', label: '20-Day Lower BB' },
  
  // Fibonacci Levels
  { key: 'FIB_SWING_PERIOD', label: 'Last 10%+ Swing Lasting 10+ Days' },
  { key: 'FIB_SWING_PERCENTAGE', label: 'Swing %' },
  { key: 'FIB_0_0', label: '0% Retracement' },
  { key: 'FIB_0_236', label: '23.6% Retracement' },
  { key: 'FIB_0_382', label: '38.2% Retracement' },
  { key: 'FIB_0_5', label: '50% Retracement' },
  { key: 'FIB_0_618', label: '61.8% Retracement' },
  { key: 'FIB_0_786', label: '78.6% Retracement' },
  { key: 'FIB_1_0', label: '100% Retracement' },
];

// Category headers for organizing statistics (same as ListTechnicals)
const CATEGORY_HEADERS = [
  { startIndex: 0, label: 'Range Analysis' },
  { startIndex: 5, label: 'Simple Moving Average' },
  { startIndex: 13, label: 'Volume Moving Average' },
  { startIndex: 19, label: 'Exponential Moving Average' },
  { startIndex: 25, label: 'Relative Strength Index' },
  { startIndex: 28, label: 'MACD' },
  { startIndex: 31, label: 'Stochastic Oscillator' },
  { startIndex: 37, label: 'Bollinger Bands' },
  { startIndex: 40, label: 'Fibonacci Levels' },
];

// Date columns for the table (imported from tickerTechnicalsUtils)
const DATE_COLUMNS = TECHNICALS_DATE_COLUMNS;

interface TechnicalData {
  ticker_basics_id: number;
  indicatorKey: string;
  indicatorValue: number | null;
  indicatorNonNumericValue: string | null;
  indicatorCategory: string | null;
  daysAgo: number;
  date: string;
}

interface TickerTechnicalData {
  ticker_symbol: string;
  ticker_basics_id: number;
  indicators: TechnicalData[];
}

// DailyPriceData is imported from tickerTechnicalsUtils

interface TickerTechnicalsResponse {
  success: boolean;
  message: string;
  data: TickerTechnicalData[];
  metadata: {
    queryDate: Date;
    daysAgo: number[];
    tickerCount: number;
    totalIndicators: number;
  };
}

const TickerTechnicals: React.FC = () => {
  const { ticker } = useParams<{ ticker: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const { fontSize } = useFontSize();
  
  // Pro-only statistics (always show PRO tag, regardless of subscription status)
  const PRO_ONLY_STATISTICS = [
    'SMA_W200',
    'MACD_LINE',
    'MACD_SIGNAL',
    'FIB_0_236',
    'FIB_0_382',
    'FIB_0_5',
    'FIB_0_618',
    'FIB_0_786',
  ];
  
  // Check for public_uuid query parameter
  const searchParams = new URLSearchParams(location.search);
  const publicUuid = searchParams.get('public_uuid');
  const isPublicList = !!publicUuid;
  
  // Public list state
  const [publicListData, setPublicListData] = useState<{
    ownerHasProSubscription: boolean;
  } | null>(null);
  
  // Determine locked statistics based on pro subscription status (for actually locking values)
  // For public lists, use owner's subscription status instead of viewer's
  const lockedStatistics = React.useMemo(() => {
    if (isPublicList && publicListData) {
      return publicListData.ownerHasProSubscription ? [] : PRO_ONLY_STATISTICS;
    }
    return (!isAuthenticated || !user?.hasProSubscription) ? PRO_ONLY_STATISTICS : [];
  }, [isPublicList, publicListData, isAuthenticated, user?.hasProSubscription]);
  
  const [tickerData, setTickerData] = useState<TickerTechnicalData | null>(null);
  const [tickerName, setTickerName] = useState<string>(() => {
    const cachedName = sessionStorage.getItem(`ticker-name-${ticker}`);
    return cachedName || '';
  });
  const [tickerSymbol, setTickerSymbol] = useState<string>(() => {
    const cachedSymbol = sessionStorage.getItem(`ticker-symbol-${ticker}`);
    return cachedSymbol ?? ticker ?? '';
  });
  const [tickerBasicsId, setTickerBasicsId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [dates, setDates] = useState<{ [daysAgo: number]: string }>({});
  const [dailyPrices, setDailyPrices] = useState<{ [daysAgo: number]: DailyPriceData }>({});

  // Set document title and meta description for ticker technicals page
  useTickerPageMeta({
    tickerSymbol,
    tickerName,
    pageType: 'technicals',
  });
  
  // Edit mode state
  const [isEditingColumns, setIsEditingColumns] = useState(false);
  const [hiddenColumns, setHiddenColumns] = useState<number[]>(() => {
    const saved = sessionStorage.getItem(`stocktable-ticker-technicals-hidden-columns-${ticker}`);
    if (saved) {
      return JSON.parse(saved);
    }
    // No default hidden columns - wait for preferences to be retrieved
    return [];
  });
  const [hiddenStatistics, setHiddenStatistics] = useState<string[]>(() => {
    const saved = sessionStorage.getItem(`stocktable-ticker-technicals-hidden-statistics-${ticker}`);
    return saved ? JSON.parse(saved) : [];
  });
  
  // Temporary state for editing mode (not saved until "Done" is clicked)
  const [tempHiddenColumns, setTempHiddenColumns] = useState<number[]>([]);
  const [tempHiddenStatistics, setTempHiddenStatistics] = useState<string[]>([]);
  
  // Polling state
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncPollingAttempts, setSyncPollingAttempts] = useState<number>(0);
  const [pollingLimitReached, setPollingLimitReached] = useState(false);
  
  // Category info modal state
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const pollingStoppedRef = useRef(false);
  const syncPollingIntervalRef = useRef<NodeJS.Timeout | null>(null); // Use ref for synchronous access
  const isCheckingSyncRef = useRef(false); // Flag to prevent concurrent sync checks
  const currentAttemptRef = useRef(1); // Track current attempt number for interval callback
  
  // Full screen state
  const [isFullScreen, setIsFullScreen] = useState(false);
  useBodyScrollLock(isFullScreen);

  // Fetch public list data when publicUuid is present
  useEffect(() => {
    if (publicUuid && !publicListData) {
      publicListApi.getPublicList(publicUuid)
        .then((response) => {
          setPublicListData({
            ownerHasProSubscription: response.data.owner_has_pro_subscription,
          });
        })
        .catch((error) => {
          console.error('Failed to fetch public list:', error);
          // If public list fetch fails, treat as if owner doesn't have PRO
          setPublicListData({
            ownerHasProSubscription: false,
          });
        });
    } else if (!publicUuid) {
      setPublicListData(null);
    }
  }, [publicUuid, publicListData]);
  
  // Handle keyboard events for full screen mode
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFullScreen) {
        setIsFullScreen(false);
      }
    };

    if (isFullScreen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullScreen]);

  // Function to clear interval without setting stopped flag (for cleanup/reset)
  const clearPollingInterval = () => {
    if (syncPollingIntervalRef.current) {
      clearInterval(syncPollingIntervalRef.current);
      syncPollingIntervalRef.current = null;
    }
  };

  // Function to stop polling
  const stopPolling = () => {
    pollingStoppedRef.current = true;
    clearPollingInterval();
    setIsSyncing(false);
  };

  // Function to check sync status and fetch data
  const checkSyncAndFetchData = async (attemptNumber: number = 1) => {
    if (!ticker) return;
    
    // Guard: Check if we've exceeded the attempt limit before proceeding
    if (attemptNumber > 5) {
      console.warn(`TickerTechnicals: Attempt ${attemptNumber} exceeds limit (5), stopping`);
      stopPolling();
      setPollingLimitReached(true);
      isCheckingSyncRef.current = false;
      return;
    }
    
    // Guard: Don't proceed if polling has been stopped (e.g., after reaching limit or 429 error)
    if (pollingStoppedRef.current) {
      console.log(`TickerTechnicals: checkSyncAndFetchData called but polling is stopped, ignoring`);
      isCheckingSyncRef.current = false;
      return;
    }
    
    // Guard: Prevent concurrent execution - if a sync check is already in progress, ignore this call
    if (isCheckingSyncRef.current) {
      console.log(`TickerTechnicals: checkSyncAndFetchData already in progress, ignoring duplicate call (attempt ${attemptNumber})`);
      return;
    }
    
    // Set flag to prevent concurrent calls
    isCheckingSyncRef.current = true;
    
    console.log(`TickerTechnicals: checkSyncAndFetchData called for ${ticker} (attempt ${attemptNumber})`);
    
    // Update global polling attempts for display purposes
    setSyncPollingAttempts(attemptNumber);

    try {
      console.log(`🚀 TickerTechnicals: About to call checkTickerSyncStatus for: ${ticker}`);
      const syncResponse = await tickerApi.checkTickerSyncStatus({
        ticker_symbols: [ticker],
      });
      
      console.log(`TickerTechnicals: Sync response for ${ticker}:`, syncResponse);
      
      const tickerInfo = syncResponse.data.tickers.find((t: any) => t.ticker_symbol === ticker);
      if (!tickerInfo) {
        throw new Error(`Ticker ${ticker} not found`);
      }
      
      const name = tickerInfo.ticker_name || ticker;
      const symbol = tickerInfo.ticker_symbol || ticker;
      setTickerName(name);
      setTickerSymbol(symbol);
      sessionStorage.setItem(`ticker-name-${ticker}`, name);
      sessionStorage.setItem(`ticker-symbol-${ticker}`, symbol);
      
      // Set lastUpdated from last_sync_ended_at
      if (tickerInfo.last_sync_ended_at) {
        const date = typeof tickerInfo.last_sync_ended_at === 'string' 
          ? new Date(tickerInfo.last_sync_ended_at) 
          : tickerInfo.last_sync_ended_at;
        if (date instanceof Date && !isNaN(date.getTime())) {
          setLastUpdated(date);
        }
      }
      
      // Set tickerBasicsId immediately after getting sync response
      setTickerBasicsId(tickerInfo.ticker_basics_id);
      
      // Load user ticker preferences NOW (right after getting tickerBasicsId)
      if (isAuthenticated && tickerInfo.ticker_basics_id) {
        try {
          console.log('🚀 Loading preferences immediately after getting tickerBasicsId');
          const preferences = await loadUserTickerPreferences(tickerInfo.ticker_basics_id, 'technicals');
          setHiddenStatistics(preferences.hiddenStatistics); // Always set, even if empty array
          setHiddenColumns(preferences.hiddenDaysAgo || []); // Always set, even if empty array
          
          if (preferences.hiddenStatistics.length > 0) {
            console.log('✅ Loaded technical ticker preferences:', preferences.hiddenStatistics);
          } else {
            console.log('✅ No technical ticker preferences found, showing all indicators');
          }
          
          if (preferences.hiddenDaysAgo && preferences.hiddenDaysAgo.length > 0) {
            console.log('✅ Loaded technical days ago preferences:', preferences.hiddenDaysAgo);
          } else {
            console.log('✅ No technical days ago preferences found, showing all columns');
          }
        } catch (error) {
          console.log('No technical ticker preferences found or error loading:', error);
          setHiddenStatistics([]); // Reset to show all indicators when no preferences
          setHiddenColumns([]); // Reset to show all columns when no preferences
        }
      }
      
      // Check if ticker is synced
      if (tickerInfo.is_synced) {
        console.log(`TickerTechnicals: ${ticker} is synced, fetching data`);
        
        // Fetch technical data
        // Use authenticated API if user is logged in, otherwise use unauthenticated API
        const api = isAuthenticated ? userTickerApi : tickerApi;
        const daysAgoArray = DATE_COLUMNS.map(col => col.daysAgo);
        const response: TickerTechnicalsResponse = await api.getTickerTechnical({
          tickerBasicsIds: [tickerInfo.ticker_basics_id],
          daysAgoArray: daysAgoArray,
          ownerHasProSubscription: isPublicList && publicListData ? publicListData.ownerHasProSubscription : undefined,
        });
        
        if (response.data && response.data.length > 0) {
          setTickerData(response.data[0]);
          
          // Extract dates for each daysAgo
          const dateMap: { [daysAgo: number]: string } = {};
          response.data[0].indicators.forEach(indicator => {
            if (!dateMap[indicator.daysAgo]) {
              dateMap[indicator.daysAgo] = new Date(indicator.date).toLocaleDateString();
            }
          });
          setDates(dateMap);
          
          // Fetch daily prices for closing price calculations
          await fetchDailyPrices(ticker, tickerInfo.ticker_basics_id);
        } else {
          throw new Error('No technical data found for this ticker');
        }
        
        // Stop polling since we got the data
        stopPolling();
        setLoading(false);
        setError(null);
        isCheckingSyncRef.current = false; // Reset flag
      } else {
        // If we've reached the polling limit, try to fetch data anyway
        if (attemptNumber >= 5) {
          console.warn(`Sync polling limit reached (5 attempts) for ticker: ${ticker}. Attempting to fetch data anyway.`);
          setPollingLimitReached(true);
          stopPolling();
          
          // Still try to fetch technical data even though sync status is false
          try {
            // Use authenticated API if user is logged in, otherwise use unauthenticated API
            const api = isAuthenticated ? userTickerApi : tickerApi;
            const daysAgoArray = DATE_COLUMNS.map(col => col.daysAgo);
            const response: TickerTechnicalsResponse = await api.getTickerTechnical({
              tickerBasicsIds: [tickerInfo.ticker_basics_id],
              daysAgoArray: daysAgoArray,
              ownerHasProSubscription: isPublicList && publicListData ? publicListData.ownerHasProSubscription : undefined,
            });
            
            if (response.data && response.data.length > 0) {
              setTickerData(response.data[0]);
              
              // Extract dates for each daysAgo
              const dateMap: { [daysAgo: number]: string } = {};
              response.data[0].indicators.forEach(indicator => {
                if (!dateMap[indicator.daysAgo]) {
                  dateMap[indicator.daysAgo] = new Date(indicator.date).toLocaleDateString();
                }
              });
              setDates(dateMap);
              
              // Fetch daily prices for closing price calculations
              await fetchDailyPrices(ticker, tickerInfo.ticker_basics_id);
              
              setLoading(false);
              setError(null);
              console.log(`TickerTechnicals: Successfully fetched data for ${ticker} despite sync status being false`);
            } else {
              throw new Error('No technical data found for this ticker');
            }
          } catch (fetchErr) {
            console.error(`TickerTechnicals: Failed to fetch data for ${ticker} after polling limit:`, fetchErr);
            setError(fetchErr instanceof Error ? fetchErr.message : 'Failed to fetch ticker data');
            setLoading(false);
          }
          isCheckingSyncRef.current = false; // Reset flag
        } else {
          // Double-check we haven't stopped polling before setting up interval
          if (pollingStoppedRef.current) {
            console.log(`TickerTechnicals: Polling stopped, not setting up interval`);
            isCheckingSyncRef.current = false; // Reset flag before returning
            return;
          }
          
          console.log(`TickerTechnicals: ${ticker} is not synced yet (attempt ${attemptNumber}/5), continuing to poll`);
          setIsSyncing(true);
          
          // Update the current attempt ref so the interval callback knows the next attempt number
          currentAttemptRef.current = attemptNumber;
          
          // Continue polling with 2 second delay
          const pollingDelay = 2000; // 2 seconds
          
          // Clear any existing interval before creating a new one
          // This ensures we always use the latest attempt number
          clearPollingInterval();
          
          console.log(`Starting sync polling with ${pollingDelay}ms delay`);
          const interval = setInterval(() => {
            // Check if polling was stopped or we've reached the limit
            if (pollingStoppedRef.current) {
              clearInterval(interval);
              syncPollingIntervalRef.current = null;
              return;
            }
            
            // Get the next attempt number from the ref
            const nextAttempt = currentAttemptRef.current + 1;
            
            // Check if we've exceeded the limit
            if (nextAttempt > 5) {
              console.log(`TickerTechnicals: Reached attempt limit (5), stopping interval`);
              clearInterval(interval);
              syncPollingIntervalRef.current = null;
              pollingStoppedRef.current = true;
              return;
            }
            
            // Update the ref for the next iteration
            currentAttemptRef.current = nextAttempt;
            
            // Make the sync check call
            checkSyncAndFetchData(nextAttempt);
          }, pollingDelay);
          
          syncPollingIntervalRef.current = interval;
          
          // Reset flag after setting up interval
          isCheckingSyncRef.current = false;
        }
      }
    } catch (err) {
      console.error(`TickerTechnicals: Error in checkSyncAndFetchData:`, err);
      
      // Check if it's a 429 rate limit error
      const isRateLimitError = 
        (axios.isAxiosError(err) && err.response?.status === 429) ||
        (err instanceof Error && (err.message.includes('429') || err.message.includes('rate limit') || err.message.includes('Rate limit')));
      
      if (isRateLimitError) {
        console.warn(`TickerTechnicals: Rate limit (429) detected for ${ticker}. Stopping polling and attempting to fetch data anyway.`);
        stopPolling();
        setPollingLimitReached(true);
        
        // Still try to fetch technical data even after rate limit error
        try {
          if (tickerBasicsId) {
            // Use authenticated API if user is logged in, otherwise use unauthenticated API
            const api = isAuthenticated ? userTickerApi : tickerApi;
            const daysAgoArray = DATE_COLUMNS.map(col => col.daysAgo);
            const response: TickerTechnicalsResponse = await api.getTickerTechnical({
              tickerBasicsIds: [tickerBasicsId],
              daysAgoArray: daysAgoArray,
              ownerHasProSubscription: isPublicList && publicListData ? publicListData.ownerHasProSubscription : undefined,
            });
            
            if (response.data && response.data.length > 0) {
              setTickerData(response.data[0]);
              
              // Extract dates for each daysAgo
              const dateMap: { [daysAgo: number]: string } = {};
              response.data[0].indicators.forEach(indicator => {
                if (!dateMap[indicator.daysAgo]) {
                  dateMap[indicator.daysAgo] = new Date(indicator.date).toLocaleDateString();
                }
              });
              setDates(dateMap);
              
              // Fetch daily prices for closing price calculations
              await fetchDailyPrices(ticker, tickerBasicsId);
              
              setLoading(false);
              setError(null);
              console.log(`TickerTechnicals: Successfully fetched data for ${ticker} despite rate limit error`);
            } else {
              throw new Error('No technical data found for this ticker');
            }
          } else {
            throw new Error('Ticker basics ID not available');
          }
        } catch (fetchErr) {
          console.error(`TickerTechnicals: Failed to fetch data for ${ticker} after rate limit error:`, fetchErr);
          setError('Rate limit exceeded. Please try again later.');
          setLoading(false);
        }
        isCheckingSyncRef.current = false; // Reset flag
      } else {
        // For other errors, set error state normally (show 404 for invalid/non-existent ticker)
        const rawMessage = err instanceof Error ? err.message : 'Failed to fetch ticker data';
        const isTickerNotFound = /no valid ticker|valid tickers found/i.test(rawMessage);
        setError(isTickerNotFound ? '404 - Ticker not found' : rawMessage);
        stopPolling();
        setLoading(false);
        isCheckingSyncRef.current = false; // Reset flag
      }
    }
  };

  useEffect(() => {
    if (!ticker) return;

    // Reset polling state when ticker changes
    pollingStoppedRef.current = false;
    setPollingLimitReached(false);
    setSyncPollingAttempts(0);
    currentAttemptRef.current = 1; // Reset attempt counter
    isCheckingSyncRef.current = false; // Reset concurrent check flag
    clearPollingInterval(); // Clear any existing interval without setting stopped flag

    // For public lists, wait for publicListData to be loaded before fetching
    if (isPublicList && !publicListData) {
      return; // Wait for publicListData to load
    }

    // Start the polling process (preferences will be loaded inside checkSyncAndFetchData)
    setLoading(true);
    checkSyncAndFetchData(1);
  }, [ticker, isPublicList, publicListData]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);



  const handleBackClick = () => {
    if (publicUuid) {
      navigate(`/technicals?public_uuid=${publicUuid}`);
    } else {
      navigate('/technicals');
    }
  };

  // Category info modal handlers
  const handleCategoryInfoClick = (categoryName: string) => {
    setSelectedCategory(categoryName);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCategory('');
  };

  // Function to fetch daily prices for the ticker
  const fetchDailyPrices = async (tickerSymbol: string, tickerBasicsId: number) => {
    try {
      const daysAgoArray = DATE_COLUMNS.map(col => col.daysAgo);
      const response = await tickerApi.getTickerDailyPrices(
        tickerSymbol, 
        0, // page
        100, // limit - get enough to cover all days
        '5years', // timeframe
        'date', // sortColumn
        'desc' // sortDirection
      );

      // Create a map of daysAgo to price data
      const priceMap: { [daysAgo: number]: DailyPriceData } = {};
      
      // Since daily_prices is sorted by date descending (most recent first),
      // we can use the index directly to match daysAgo
      // Index 0 = daysAgo 0 (today), index 1 = daysAgo 1 (yesterday), etc.
      daysAgoArray.forEach(daysAgo => {
        // The daily_prices array is sorted by date descending, so index 'x' corresponds to 'x' days ago
        // from the most recent date in the fetched data.
        if (daysAgo < response.data.daily_prices.length) {
          const priceData = response.data.daily_prices[daysAgo];
          priceMap[daysAgo] = {
            date: priceData.date,
            close_price_adjusted: priceData.close_price_adjusted
          };
        }
      });
      
      setDailyPrices(priceMap);
    } catch (error) {
      console.error('Error fetching daily prices:', error);
      // Don't throw error, just log it - technical data can still be displayed
    }
  };

  // State to track screen size for responsive column width
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 640);
  
  // Update screen size on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 640);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate statistics column width based on longest indicator name (same logic as Stock Table)
  const statisticsColumnWidth = calculateTickerTechnicalsStatisticsColumnWidth(
    isEditingColumns,
    isSmallScreen
  );
  
  // Debug logging
  console.log('TickerTechnicals - Statistics column width calculated:', statisticsColumnWidth);
  console.log('TickerTechnicals - Is small screen:', isSmallScreen);
  console.log('TickerTechnicals - Window width:', window.innerWidth);
  console.log('TickerTechnicals - Longest label:', TECHNICAL_STATISTICS.reduce((longest, stat) => 
    stat.label.length > longest.length ? stat.label : longest, ''
  ));

  // Edit mode handlers
  const handleStartEdit = () => {
    // Initialize temporary state with current saved state
    setTempHiddenColumns([...hiddenColumns]);
    setTempHiddenStatistics([...hiddenStatistics]);
    setIsEditingColumns(true);
  };

  const handleCancelEdit = () => {
    // Reset temporary state
    setTempHiddenColumns([]);
    setTempHiddenStatistics([]);
    setIsEditingColumns(false);
  };

  const handleDoneEdit = async () => {
    // Save temporary state to permanent state and sessionStorage
    setHiddenColumns([...tempHiddenColumns]);
    setHiddenStatistics([...tempHiddenStatistics]);
    sessionStorage.setItem(`stocktable-ticker-technicals-hidden-columns-${ticker}`, JSON.stringify(tempHiddenColumns));
    sessionStorage.setItem(`stocktable-ticker-technicals-hidden-statistics-${ticker}`, JSON.stringify(tempHiddenStatistics));
    
    // Save user ticker preferences for authenticated users (always save, even if empty arrays)
    if (isAuthenticated && tickerBasicsId) {
      try {
        await saveUserTickerPreferences(tickerBasicsId, 'technicals', tempHiddenStatistics, tempHiddenColumns);
        console.log('✅ Technical ticker preferences saved successfully');
      } catch (error) {
        console.error('❌ Failed to save technical ticker preferences:', error);
        // Don't throw error to prevent UI from breaking
      }
    }
    
    // Reset temporary state
    setTempHiddenColumns([]);
    setTempHiddenStatistics([]);
    setIsEditingColumns(false);
  };

  // Column visibility handlers (work with temporary state in edit mode)
  const handleHideColumn = (daysAgo: number) => {
    if (isEditingColumns) {
      setTempHiddenColumns(prev => [...prev, daysAgo]);
    } else {
      setHiddenColumns(prev => [...prev, daysAgo]);
    }
  };

  const handleShowColumn = (daysAgo: number) => {
    if (isEditingColumns) {
      setTempHiddenColumns(prev => prev.filter(d => d !== daysAgo));
    } else {
      setHiddenColumns(prev => prev.filter(d => d !== daysAgo));
    }
  };

  // Statistics visibility handlers (work with temporary state in edit mode)
  const handleHideStatistic = (statisticKey: string) => {
    if (isEditingColumns) {
      setTempHiddenStatistics(prev => [...prev, statisticKey]);
    } else {
      setHiddenStatistics(prev => [...prev, statisticKey]);
    }
  };

  const handleShowStatistic = (statisticKey: string) => {
    if (isEditingColumns) {
      setTempHiddenStatistics(prev => prev.filter(key => key !== statisticKey));
    } else {
      setHiddenStatistics(prev => prev.filter(key => key !== statisticKey));
    }
  };

  // Determine available columns based on actual data
  // During loading/retry, show default columns to maintain proper table layout
  const availableColumns = tickerData && tickerData.indicators.length > 0
    ? DATE_COLUMNS.filter(col => {
        // Check if any indicator has data for this daysAgo
        return tickerData.indicators.some(indicator => indicator.daysAgo === col.daysAgo);
      })
    : (loading || isSyncing ? DATE_COLUMNS : []); // Show default columns during loading/retry

  // Filter visible columns and statistics
  const visibleColumns = isEditingColumns 
    ? availableColumns 
    : availableColumns.filter(col => !hiddenColumns.includes(col.daysAgo));
  const visibleStatistics = isEditingColumns 
    ? TECHNICAL_STATISTICS 
    : TECHNICAL_STATISTICS.filter(stat => !hiddenStatistics.includes(stat.key));
  
  // Get the current hidden state (temporary in edit mode, permanent in normal mode)
  const currentHiddenColumns = isEditingColumns ? tempHiddenColumns : hiddenColumns;
  const currentHiddenStatistics = isEditingColumns ? tempHiddenStatistics : hiddenStatistics;

  // Create indicator accessors from tickerData (using utilities)
  const indicatorAccessors = React.useMemo(() => {
    if (!tickerData) return null;
    
    // Convert tickerData to TickerTechnicalDataFromAPI format
    const tickerDataFromAPI: TickerTechnicalDataFromAPI = {
      ticker_symbol: tickerSymbol,
      ticker_basics_id: tickerData.ticker_basics_id,
      indicators: tickerData.indicators.map((ind) => ({
        indicatorKey: ind.indicatorKey,
        indicatorValue: ind.indicatorValue,
        indicatorNonNumericValue: ind.indicatorNonNumericValue || null,
        daysAgo: ind.daysAgo,
        date: ind.date,
        ticker_basics_id: ind.ticker_basics_id || tickerData.ticker_basics_id,
      })),
    };
    
    return createIndicatorAccessors(tickerDataFromAPI);
  }, [tickerData, tickerSymbol]);
  
  // Helper functions for backward compatibility (using accessors)
  const getIndicatorValue = (indicatorKey: string, daysAgo: number): number | null => {
    if (!indicatorAccessors) return null;
    return indicatorAccessors.getIndicatorValue(indicatorKey, daysAgo);
  };

  const getIndicatorNonNumericValue = (indicatorKey: string, daysAgo: number): string | null => {
    if (!indicatorAccessors) return null;
    return indicatorAccessors.getIndicatorNonNumericValue(indicatorKey, daysAgo);
  };

  // Function to detect golden cross for a given indicator pair (using utilities)
  const hasGoldenCross = (currentIndicator: string, compareIndicator: string): boolean => {
    if (!indicatorAccessors) return false;
    return hasGoldenCrossUtil(indicatorAccessors, currentIndicator, compareIndicator);
  };

  // Function to detect death cross for a given indicator pair (using utilities)
  const hasDeathCross = (currentIndicator: string, compareIndicator: string): boolean => {
    if (!indicatorAccessors) return false;
    return hasDeathCrossUtil(indicatorAccessors, currentIndicator, compareIndicator);
  };

  // Helper function to detect if price crossed above a Bollinger Band within past 6 days
  const hasBBGoldenCross = (bbKey: string, daysAgo: number): boolean => {
    if (!tickerData) return false;
    
    const currentPriceData = dailyPrices[daysAgo];
    const previousPriceData = dailyPrices[daysAgo + 1];
    const bbValue = getIndicatorValue(bbKey, daysAgo);
    
    if (!currentPriceData || bbValue === undefined || bbValue === null) {
      return false;
    }
    
    const currentPrice = currentPriceData.close_price_adjusted;
    
    // Current price must be above the BB
    if (currentPrice <= bbValue) {
      return false;
    }
    
    // Check if price just crossed above (previous price was below or equal)
    if (previousPriceData) {
      const previousPrice = previousPriceData.close_price_adjusted;
      return previousPrice <= bbValue;
    }
    
    // If no previous price, we can't determine if it just crossed
    return false;
  };

  // Helper function to detect if price crossed below a Bollinger Band within past 6 days
  const hasBBDeathCross = (bbKey: string, daysAgo: number): boolean => {
    if (!tickerData) return false;
    
    const currentPriceData = dailyPrices[daysAgo];
    const previousPriceData = dailyPrices[daysAgo + 1];
    const bbValue = getIndicatorValue(bbKey, daysAgo);
    
    if (!currentPriceData || bbValue === undefined || bbValue === null) {
      return false;
    }
    
    const currentPrice = currentPriceData.close_price_adjusted;
    
    // Current price must be below the BB
    if (currentPrice >= bbValue) {
      return false;
    }
    
    // Check if price just crossed below (previous price was above or equal)
    if (previousPriceData) {
      const previousPrice = previousPriceData.close_price_adjusted;
      return previousPrice >= bbValue;
    }
    
    // If no previous price, we can't determine if it just crossed
    return false;
  };

  // Helper function to get Bollinger Band icons (golden cross, death cross, support, resistance)
  const getBollingerBandIcon = (indicatorKey: string, daysAgo: number): React.ReactNode => {
    if (!tickerData) return null;
    
    const currentPriceData = dailyPrices[daysAgo];
    const bbValue = getIndicatorValue(indicatorKey, daysAgo);
    
    if (!currentPriceData || bbValue === undefined || bbValue === null) {
      return null;
    }
    
    const currentPrice = currentPriceData.close_price_adjusted;
    
    // Get all BB values for this daysAgo
    const upperBB = getIndicatorValue('BB_20_UPPER_20', daysAgo);
    const middleBB = getIndicatorValue('BB_20_SMA', daysAgo);
    const lowerBB = getIndicatorValue('BB_20_LOWER_20', daysAgo);
    
    if (upperBB === undefined || upperBB === null ||
        middleBB === undefined || middleBB === null ||
        lowerBB === undefined || lowerBB === null) {
      return null;
    }
    
    // Check for golden cross (price crossed above this band)
    if (hasBBGoldenCross(indicatorKey, daysAgo)) {
      const bandName = indicatorKey.includes('UPPER') ? 'Upper' : 
                       indicatorKey.includes('SMA') ? 'Middle' : 'Lower';
      return (
        <span className="ml-1 group/golden-cross relative">
          <GoldenCrossIcon className="w-4 h-4" />
          <div className="absolute hidden group-hover/golden-cross:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
            Price crossed above {bandName} Bollinger Band within the past 6 days
          </div>
        </span>
      );
    }
    
    // Check for death cross (price crossed below this band)
    if (hasBBDeathCross(indicatorKey, daysAgo)) {
      const bandName = indicatorKey.includes('UPPER') ? 'Upper' : 
                       indicatorKey.includes('SMA') ? 'Middle' : 'Lower';
      return (
        <span className="ml-1 group/death-cross relative">
          <DeathCrossIcon className="w-4 h-4" />
          <div className="absolute hidden group-hover/death-cross:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
            Price crossed below {bandName} Bollinger Band within the past 6 days
          </div>
        </span>
      );
    }
    
    // Determine the closest lower and higher bands
    let closestLowerBand: string | null = null;
    let closestHigherBand: string | null = null;
    
    // Find closest lower band (largest band value that is still < current price)
    if (currentPrice > lowerBB) {
      if (currentPrice >= middleBB) {
        if (currentPrice >= upperBB) {
          // Price is above all bands - upper is closest lower
          closestLowerBand = 'BB_20_UPPER_20';
        } else {
          // Price is between middle and upper - middle is closest lower
          closestLowerBand = 'BB_20_SMA';
        }
      } else {
        // Price is between lower and middle - lower is closest lower
        closestLowerBand = 'BB_20_LOWER_20';
      }
    }
    
    // Find closest higher band (smallest band value that is still > current price)
    if (currentPrice < upperBB) {
      if (currentPrice < middleBB) {
        if (currentPrice < lowerBB) {
          // Price is below all bands - lower is closest higher
          closestHigherBand = 'BB_20_LOWER_20';
        } else {
          // Price is between lower and middle - middle is closest higher
          closestHigherBand = 'BB_20_SMA';
        }
      } else {
        // Price is between middle and upper - upper is closest higher
        closestHigherBand = 'BB_20_UPPER_20';
      }
    }
    
    // Show support icon only on the closest lower band
    if (closestLowerBand === indicatorKey) {
      const bandName = indicatorKey.includes('UPPER') ? 'Upper' : 
                       indicatorKey.includes('SMA') ? 'Middle' : 'Lower';
      return (
        <span className="ml-1 group/support relative">
          <SupportIcon className="w-4 h-4" />
          <div className="absolute hidden group-hover/support:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
            Price is above {bandName} Bollinger Band (support level)
          </div>
        </span>
      );
    }
    
    // Show resistance icon only on the closest higher band
    if (closestHigherBand === indicatorKey) {
      const bandName = indicatorKey.includes('UPPER') ? 'Upper' : 
                       indicatorKey.includes('SMA') ? 'Middle' : 'Lower';
      return (
        <span className="ml-1 group/resistance relative">
          <ResistanceIcon className="w-4 h-4" />
          <div className="absolute hidden group-hover/resistance:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
            Price is below {bandName} Bollinger Band (resistance level)
          </div>
        </span>
      );
    }
    
    return null;
  };

  // Helper function to determine which Fibonacci icon to show for a given level
  const getFibonacciIcon = (indicatorKey: string, daysAgo: number): React.ReactNode => {
    if (!tickerData) return null;

    // Get swing direction
    const swingDirection = getIndicatorNonNumericValue('FIB_SWING_DIRECTION', daysAgo);
    if (!swingDirection || (swingDirection !== 'up' && swingDirection !== 'down')) {
      return null;
    }

    // Get last closing price
    const lastClosingPrice = dailyPrices[daysAgo]?.close_price_adjusted;
    if (lastClosingPrice === undefined || lastClosingPrice === null) {
      return null;
    }

    // Get current Fibonacci level value
    const currentLevelValue = getIndicatorValue(indicatorKey, daysAgo);
    if (currentLevelValue === undefined || currentLevelValue === null) {
      return null;
    }

    // Get all Fibonacci retracement levels
    const fibLevels = ['FIB_0_0', 'FIB_0_236', 'FIB_0_382', 'FIB_0_5', 'FIB_0_618', 'FIB_0_786', 'FIB_1_0'];
    const fibValues: { [key: string]: number } = {};
    
    for (const level of fibLevels) {
      const value = getIndicatorValue(level, daysAgo);
      if (value !== undefined && value !== null) {
        fibValues[level] = value;
      }
    }

    if (swingDirection === 'up') {
      // For up swing:
      // - Death cross icon: retracement level with lowest value that is higher than last closing price
      // - Support icon: retracement level with highest value that is lower than last closing price
      
      // Find levels higher than closing price
      const levelsAbove = Object.entries(fibValues)
        .filter(([_, value]) => value > lastClosingPrice)
        .sort(([_, a], [__, b]) => a - b); // Sort by value ascending
      
      // Find levels lower than closing price
      const levelsBelow = Object.entries(fibValues)
        .filter(([_, value]) => value < lastClosingPrice)
        .sort(([_, a], [__, b]) => b - a); // Sort by value descending

      // Show death cross icon if this is the lowest level above closing price
      if (levelsAbove.length > 0 && levelsAbove[0][0] === indicatorKey) {
        return (
          <span className="ml-1 group/death-cross relative">
            <DeathCrossIcon className="w-4 h-4" />
            <div className="absolute hidden group-hover/death-cross:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
              Last closing price broke below this support level
            </div>
          </span>
        );
      }

      // Show support icon if this is the highest level below closing price
      if (levelsBelow.length > 0 && levelsBelow[0][0] === indicatorKey) {
        return (
          <span className="ml-1 group/support relative">
            <SupportIcon className="w-4 h-4" />
            <div className="absolute hidden group-hover/support:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
              Next support level
            </div>
          </span>
        );
      }
    } else {
      // For down swing:
      // - Golden cross icon: retracement level with highest value that is lower than last closing price
      // - Resistance icon: retracement level with lowest value that is higher than last closing price
      
      // Find levels lower than closing price
      const levelsBelow = Object.entries(fibValues)
        .filter(([_, value]) => value < lastClosingPrice)
        .sort(([_, a], [__, b]) => b - a); // Sort by value descending
      
      // Find levels higher than closing price
      const levelsAbove = Object.entries(fibValues)
        .filter(([_, value]) => value > lastClosingPrice)
        .sort(([_, a], [__, b]) => a - b); // Sort by value ascending

      // Show golden cross icon if this is the highest level below closing price
      if (levelsBelow.length > 0 && levelsBelow[0][0] === indicatorKey) {
        return (
          <span className="ml-1 group/golden-cross relative">
            <GoldenCrossIcon className="w-4 h-4" />
            <div className="absolute hidden group-hover/golden-cross:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
              Last closing price broke above this resistance level
            </div>
          </span>
        );
      }

      // Show resistance icon if this is the lowest level above closing price
      if (levelsAbove.length > 0 && levelsAbove[0][0] === indicatorKey) {
        return (
          <span className="ml-1 group/resistance relative">
            <ResistanceIcon className="w-4 h-4" />
            <div className="absolute hidden group-hover/resistance:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
              Next resistance level
            </div>
          </span>
        );
      }
    }

    return null;
  };

  // Convert dailyPrices to DailyPriceData format
  const dailyPricesFormatted: { [daysAgo: number]: DailyPriceData } = React.useMemo(() => {
    const formatted: { [daysAgo: number]: DailyPriceData } = {};
    Object.keys(dailyPrices).forEach((daysAgoStr) => {
      const daysAgo = parseInt(daysAgoStr);
      const priceData = dailyPrices[daysAgo];
      if (priceData) {
        formatted[daysAgo] = {
          date: priceData.date,
          close_price_adjusted: priceData.close_price_adjusted,
        };
      }
    });
    return formatted;
  }, [dailyPrices]);
  
  // Helper function to determine color based on percentage values (using utilities)
  const getValueColor = (indicatorKey: string, daysAgo: number): string => {
    if (!indicatorAccessors) return 'text-text-light-neutral dark:text-text-secondary';
    return getTechnicalsValueColor(indicatorAccessors, dailyPricesFormatted, indicatorKey, daysAgo);
  };

  // Convert tickerData to TickerTechnicalDataFromAPI format
  const tickerDataFromAPI: TickerTechnicalDataFromAPI | null = React.useMemo(() => {
    if (!tickerData) return null;
    return {
      ticker_symbol: tickerSymbol,
      ticker_basics_id: tickerData.ticker_basics_id,
      indicators: tickerData.indicators.map((ind) => ({
        indicatorKey: ind.indicatorKey,
        indicatorValue: ind.indicatorValue,
        indicatorNonNumericValue: ind.indicatorNonNumericValue || null,
        daysAgo: ind.daysAgo,
        date: ind.date,
        ticker_basics_id: ind.ticker_basics_id || tickerData.ticker_basics_id,
      })),
    };
  }, [tickerData, tickerSymbol]);
  
  // Helper function to format statistic values (using utilities)
  const formatStatisticValue = (indicatorKey: string, daysAgo: number): React.ReactNode => {
    if (!indicatorAccessors) {
      if (loading || isSyncing) {
        return <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 w-16 rounded"></div>;
      }
      return <span className="text-gray-400 dark:text-text-secondary">-</span>;
    }
    
    // Check if this statistic is locked for non-pro users
    // If the value is null (locked), show lock icon instead of formatting
    if (lockedStatistics.includes(indicatorKey)) {
      const value = indicatorAccessors.getIndicatorValue(indicatorKey, daysAgo);
      const nonNumericValue = indicatorAccessors.getIndicatorNonNumericValue(indicatorKey, daysAgo);
      // Show lock icon if value is null (indicating it's locked)
      if (value === null || nonNumericValue === null) {
        return <LockIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" onClick={!isPublicList ? () => navigate('/pricing', { state: { fromUpgrade: true } }) : undefined} tooltipText={isPublicList ? "Locked due to the list owner's plan" : "Upgrade to the Pro Plan to see"} />;
      }
    }
    
    return formatTechnicalsStatisticValue(
      indicatorAccessors,
      dailyPricesFormatted,
      tickerDataFromAPI,
      tickerSymbol,
      indicatorKey,
      daysAgo,
      loading || isSyncing
    );
  };

  // Ref and state for detecting when "Last Closing Price" row is stuck (normal mode)
  const lastClosingPriceRowRef = useRef<HTMLTableRowElement>(null);
  const [isLastClosingPriceStuck, setIsLastClosingPriceStuck] = useState(false);
  
  // Ref and state for detecting when "Last Closing Price" row is stuck (full screen mode)
  const lastClosingPriceRowFullScreenRef = useRef<HTMLTableRowElement>(null);
  const [isLastClosingPriceStuckFullScreen, setIsLastClosingPriceStuckFullScreen] = useState(false);
  
  // Ref for table scroll container (reset horizontal scroll when switching tabs)
  const tableScrollContainerRef = useRef<HTMLDivElement>(null);
  
  // Reset horizontal scroll to left when switching tabs
  useEffect(() => {
    tableScrollContainerRef.current && (tableScrollContainerRef.current.scrollLeft = 0);
  }, [location.pathname]);
  
  // Detect when the sticky row becomes stuck using scroll event (normal mode)
  useEffect(() => {
    const row = lastClosingPriceRowRef.current;
    if (!row || isFullScreen) return; // Only check in normal mode
    
    const checkIfStuck = () => {
      if (!row) return;
      
      // Find the header row to get its position
      const table = row.closest('table');
      const thead = table?.querySelector('thead');
      const headerRect = thead?.getBoundingClientRect();
      
      const rowRect = row.getBoundingClientRect();
      const stickyTop = window.innerWidth >= 640 ? 64 : 48; // sm:top-[64px] or top-[48px]
      
      // Row is stuck if it's positioned right below the header
      let isStuck = false;
      if (headerRect) {
        const headerBottom = headerRect.bottom;
        const tolerance = 2;
        isStuck = Math.abs(rowRect.top - headerBottom) <= tolerance;
      } else {
        // Fallback: check if row is at sticky top position
        isStuck = Math.abs(rowRect.top - stickyTop) <= 3;
      }
      
      setIsLastClosingPriceStuck(isStuck);
    };
    
    // Use Intersection Observer as primary method
    const observer = new IntersectionObserver(
      (entries) => {
        checkIfStuck();
      },
      {
        root: null,
        rootMargin: `-${window.innerWidth >= 640 ? 64 : 48}px 0px 0px 0px`,
        threshold: [0, 1],
      }
    );
    
    observer.observe(row);
    
    // Also listen to scroll events as backup
    const scrollContainer = row.closest('.overflow-y-auto');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', checkIfStuck, { passive: true });
    }
    window.addEventListener('scroll', checkIfStuck, { passive: true });
    
    // Check initially and periodically
    checkIfStuck();
    const intervalId = setInterval(checkIfStuck, 100);
    
    return () => {
      observer.disconnect();
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', checkIfStuck);
      }
      window.removeEventListener('scroll', checkIfStuck);
      clearInterval(intervalId);
    };
  }, [isFullScreen]);
  
  // Detect when the sticky row becomes stuck using scroll event (full screen mode)
  useEffect(() => {
    const row = lastClosingPriceRowFullScreenRef.current;
    if (!row || !isFullScreen) return; // Only check in full screen mode
    
    const checkIfStuck = () => {
      if (!row) return;
      
      // Find the header row to get its position
      const table = row.closest('table');
      const thead = table?.querySelector('thead');
      const headerRect = thead?.getBoundingClientRect();
      
      const rowRect = row.getBoundingClientRect();
      const stickyTop = 40; // top-[40px] in full screen
      
      // Row is stuck if it's positioned right below the header
      let isStuck = false;
      if (headerRect) {
        const headerBottom = headerRect.bottom;
        const tolerance = 2;
        isStuck = Math.abs(rowRect.top - headerBottom) <= tolerance;
      } else {
        // Fallback: check if row is at sticky top position
        isStuck = Math.abs(rowRect.top - stickyTop) <= 3;
      }
      
      setIsLastClosingPriceStuckFullScreen(isStuck);
    };
    
    // Use Intersection Observer as primary method
    const observer = new IntersectionObserver(
      (entries) => {
        checkIfStuck();
      },
      {
        root: null,
        rootMargin: '-40px 0px 0px 0px',
        threshold: [0, 1],
      }
    );
    
    observer.observe(row);
    
    // Also listen to scroll events as backup
    const scrollContainer = row.closest('.h-full.overflow-auto');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', checkIfStuck, { passive: true });
    }
    window.addEventListener('scroll', checkIfStuck, { passive: true });
    
    // Check initially and periodically
    checkIfStuck();
    const intervalId = setInterval(checkIfStuck, 100);
    
    return () => {
      observer.disconnect();
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', checkIfStuck);
      }
      window.removeEventListener('scroll', checkIfStuck);
      clearInterval(intervalId);
    };
  }, [isFullScreen]);

  const tabs = (
    <TabBar 
      activeTab="technicals" 
      ticker={ticker}
      publicUuid={publicUuid}
    />
  );


  if (error) {
    return (
      <>
      <div className="flex flex-col min-h-screen bg-background-light dark:bg-background">
        <Header sticky={false} />
        {publicUuid && !isAuthenticated && <PublicListPromoBar />}
        <main className="flex-1 p-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-red-600 dark:text-red-400 mb-2">Error</h2>
              <p className="text-gray-600 dark:text-text-secondary">{error}</p>
            </div>
          </div>
        </main>
        <div className="mt-4 sm:mt-8">
          <Footer />
        </div>
      </div>
      </>
    );
  }



  return (
    <>
    <style dangerouslySetInnerHTML={{
      __html: `
        /* Sticky header bottom border - using a class-based approach */
        table thead.sticky-header-border::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 1px;
          background-color: rgb(229, 231, 235);
          z-index: 51;
          pointer-events: none;
        }
        
        .dark table thead.sticky-header-border::after {
          background-color: rgb(55, 65, 81);
        }
        
        /* Alternative: target thead with position sticky using attribute selector */
        table thead[style*="sticky"]::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 1px;
          background-color: rgb(229, 231, 235);
          z-index: 51;
          pointer-events: none;
        }
        
        .dark table thead[style*="sticky"]::after {
          background-color: rgb(55, 65, 81);
        }
        
        /* Disable transitions on background colors for data cells to prevent flashing */
        table tbody td {
          transition: none !important;
        }
        
        /* Sticky "Last Closing Price" row bottom border - using box-shadow, only when stuck */
        table tbody tr.sticky-last-closing-price.is-stuck {
          box-shadow: 0 1px 0 0 rgb(229, 231, 235) !important;
        }
        
        .dark table tbody tr.sticky-last-closing-price.is-stuck {
          box-shadow: 0 1px 0 0 rgb(55, 65, 81) !important;
        }
      `
    }} />
    <div className="flex flex-col min-h-screen bg-background-light dark:bg-background">
      {!isFullScreen && <Header sticky={false} />}
      {!isFullScreen && publicUuid && !isAuthenticated && <PublicListPromoBar />}

      <main className="px-1 lg:px-2 xl:px-4 pt-4 sm:pt-8 pb-4 sm:pb-8">
        <div className="bg-surface-light dark:bg-background-card border border-border-light dark:border-border-color rounded-md shadow-lg">
          {/* Back button */}
          <div className="p-4">
            <button
              onClick={handleBackClick}
              className={`flex items-center ${getTextSizeClass(fontSize)} text-text-light-neutral dark:text-text-secondary hover:text-text-light-primary dark:hover:text-text-primary transition-colors`}
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to List
            </button>
          </div>
          
          {/* Title */}
          <div className="px-4 pb-4">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-semibold text-text-light-primary dark:text-text-dark-primary">
                {tickerName} ({tickerSymbol})
              </h1>
              {!isEditingColumns ? (
                <button
                  onClick={handleStartEdit}
                  className="transition-colors duration-200 text-gray-600 dark:text-text-secondary hover:text-gold-text-light dark:hover:text-highlight-gold"
                  title="Edit columns"
                >
                  <svg className="lucide lucide-pencil" fill="none" height="20" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="20" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"></path>
                    <path d="m15 5 4 4"></path>
                  </svg>
                </button>
              ) : (
                <div className="flex items-center gap-4">
                  <button
                    onClick={handleCancelEdit}
                    className="text-text-light-neutral dark:text-text-secondary hover:text-text-light-primary dark:hover:text-text-primary transition-colors duration-200 text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDoneEdit}
                    className="bg-primary text-gray-900 px-3 py-1.5 rounded-md text-sm font-medium hover:bg-opacity-90 transition-colors duration-200"
                  >
                    Done
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-border-color">
            <nav className="px-4">
              {tabs}
            </nav>
          </div>

          {/* Technical Data Table */}
          <div ref={tableScrollContainerRef} className="w-full overflow-x-auto overflow-y-auto rounded-b-md" style={{ maxHeight: '80vh', position: 'relative' }}>
            <table className="min-w-full font-mono" style={{ tableLayout: 'fixed', borderCollapse: 'collapse' }}>
              <colgroup>
                <col style={{ 
                  width: `${statisticsColumnWidth}px`, 
                  minWidth: isSmallScreen ? '100px' : '200px', 
                  maxWidth: '500px' 
                }} />
                {visibleColumns.map((_, index) => (
                  <col key={index} style={{ width: '150px' }} />
                ))}
              </colgroup>
              
              <thead 
                className="bg-gray-50 dark:bg-background-card sticky-header-border" 
                style={{ 
                  position: 'sticky', 
                  top: 0, 
                  zIndex: 50
                }}
              >
                <tr>
                  <th 
                    className={`py-1 px-4 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-12 sm:h-16 bg-gray-50 dark:bg-background-card`}
                    style={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 40
                    }}
                  >
                    <div className="flex items-center">
                      <span>INDICATORS</span>
                    </div>
                  </th>
                  {visibleColumns.map((column) => (
                    <th
                      key={column.daysAgo}
                      className={`py-1 px-1 sm:px-2 md:px-4 lg:px-6 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-12 sm:h-16 bg-gray-50 dark:bg-background-card ${isEditingColumns && currentHiddenColumns.includes(column.daysAgo) ? 'opacity-50' : ''}`}
                      style={{ 
                        opacity: isEditingColumns && currentHiddenColumns.includes(column.daysAgo) ? 0.5 : 1
                      }}
                    >
                      <div className="flex flex-col items-start">
                        <div className="flex items-center gap-2">
                          {isEditingColumns && (
                            <button
                              onClick={() => currentHiddenColumns.includes(column.daysAgo) ? handleShowColumn(column.daysAgo) : handleHideColumn(column.daysAgo)}
                              className={`transition-all duration-200 p-1 rounded-full active:scale-90 ${
                                currentHiddenColumns.includes(column.daysAgo)
                                  ? 'text-gray-600 dark:text-text-secondary hover:text-highlight-gold hover:bg-highlight-gold/10' 
                                  : 'text-gray-600 dark:text-text-secondary hover:text-market-red hover:bg-market-red/10'
                              }`}
                              title={currentHiddenColumns.includes(column.daysAgo) ? "Show column" : "Hide column"}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                {currentHiddenColumns.includes(column.daysAgo) ? (
                                  // Unchecked rounded square (hidden)
                                  <rect x="2" y="2" width="20" height="20" rx="3" ry="3" strokeWidth="2" />
                                ) : (
                                  // Checked rounded square (visible)
                                  <>
                                    <rect x="2" y="2" width="20" height="20" rx="3" ry="3" strokeWidth="2" fill="white" className="dark:fill-gray-800" />
                                    <path d="M9 12l2 2 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  </>
                                )}
                              </svg>
                            </button>
                          )}
                          <span>{column.label}</span>
                        </div>
                        <span className="text-xs text-gray-500 dark:text-text-secondary font-normal">
                          {dates[column.daysAgo] || <span className="text-gray-400 dark:text-text-secondary">-</span>}
                        </span>
                      </div>
                    </th>
                  ))}
                  
                  {/* Full screen button column */}
                  <th 
                    className="py-1 px-2 sm:px-4 text-right bg-gray-50 dark:bg-background-card"
                    style={{ position: 'sticky', right: 0, zIndex: 40 }}
                  >
                    {!isEditingColumns && (
                      <button
                        onClick={() => setIsFullScreen(true)}
                        className="p-1.5 text-gray-500 dark:text-white dark:hover:text-highlight-gold transition-colors duration-200 hover:bg-gray-200 dark:hover:bg-border-color rounded-md"
                        title="Open in full screen"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                        </svg>
                      </button>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-surface-light dark:bg-background-card divide-y divide-gray-200 dark:divide-border-color">
                {(() => {
                  // Track which section headers have already been shown to avoid duplicates
                  const shownHeaders = new Set<string>();
                  
                  return visibleStatistics.map((statistic, index) => {
                    const isHidden = currentHiddenStatistics.includes(statistic.key);
                    // Find the original index of this statistic in the full statistics array
                    const originalIndex = TECHNICAL_STATISTICS.findIndex(s => s.key === statistic.key);
                    
                    // Check if we need to render a category header before this statistic
                    const categoryHeader = CATEGORY_HEADERS.find(header => {
                      // Skip if this header has already been shown
                      if (shownHeaders.has(header.label)) {
                        return false;
                      }
                      
                      // Check if this statistic belongs to this section (is at or after startIndex)
                      const belongsToSection = originalIndex >= header.startIndex;
                      
                      // Check if this is the first visible statistic in this section
                      const isFirstVisibleInSection = !TECHNICAL_STATISTICS
                        .slice(header.startIndex, originalIndex)
                        .some(s => !currentHiddenStatistics.includes(s.key));
                      
                      // If this is the first visible statistic in the section, mark the header as shown
                      if (belongsToSection && isFirstVisibleInSection) {
                        shownHeaders.add(header.label);
                      }
                      
                      return belongsToSection && isFirstVisibleInSection;
                    });
                  
                    return (
                      <React.Fragment key={statistic.key}>
                        {/* Category header row */}
                        {categoryHeader && (
                          <tr className="bg-gray-100 dark:bg-border-color">
                            <td 
                              className="py-1 sm:py-2 px-4 text-gray-900 dark:text-text-primary font-semibold text-sm bg-gray-100 dark:bg-border-color"
                              style={{ 
                                position: 'sticky', 
                                left: 0, 
                                zIndex: 30,
                                width: `${statisticsColumnWidth}px`,
                                maxWidth: `${statisticsColumnWidth}px`
                              }}
                              title={categoryHeader.label}
                            >
                              <div 
                                className="whitespace-nowrap"
                                style={{ overflow: 'visible', position: 'relative' }}
                              >
                                <span className="inline">{categoryHeader.label}</span>
                                <button
                                  onClick={() => handleCategoryInfoClick(categoryHeader.label)}
                                  className="inline-flex items-center justify-center w-5 h-5 ml-1 rounded-full text-gray-900 dark:text-text-primary hover:text-primary dark:hover:text-highlight-gold hover:bg-primary/10 dark:hover:bg-highlight-gold/10 transition-colors align-middle"
                                  title={`Learn more about ${categoryHeader.label}`}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                                    <path d="M12 16v-4" strokeWidth="2" strokeLinecap="round"/>
                                    <path d="M12 8h.01" strokeWidth="2" strokeLinecap="round"/>
                                  </svg>
                                </button>
                              </div>
                            </td>
                            <td 
                              colSpan={visibleColumns.length}
                              className="px-1 sm:px-2 md:px-4 lg:px-6 py-1 sm:py-2 text-sm font-semibold text-gray-700 dark:text-text-primary border-b border-gray-200 dark:border-border-color bg-gray-100 dark:bg-border-color"
                            >
                              {/* Empty cell for the rest of the columns */}
                            </td>
                            
                            {/* Full screen button column for category header */}
                            <td className="px-2 sm:px-4 py-1 sm:py-2 text-sm font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color">
                              {/* Empty cell for full screen button column */}
                            </td>
                          </tr>
                        )}
                        
                        {/* Statistic row */}
                        <tr 
                          ref={statistic.key === 'last_closing_price' ? lastClosingPriceRowRef : null}
                          className={`h-12 sm:h-16 ${statistic.key === 'last_closing_price' ? `sticky top-[47px] sm:top-[63px] z-40 sticky-last-closing-price ${isLastClosingPriceStuck ? 'is-stuck' : ''}` : ''} ${isEditingColumns && isHidden ? 'opacity-50' : ''}`}
                        >
                          <td 
                            className={`px-4 text-gray-900 dark:text-text-primary font-medium ${statistic.key === 'last_closing_price' && isLastClosingPriceStuck ? 'bg-blue-50 dark:bg-gray-800' : 'bg-surface-light dark:bg-background-card'}`}
                            style={{ 
                              position: 'sticky', 
                              left: 0, 
                              zIndex: 30,
                              width: `${statisticsColumnWidth}px`,
                              maxWidth: `${statisticsColumnWidth}px`,
                              paddingTop: '0px',
                              paddingBottom: '0px',
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word',
                              hyphens: 'auto'
                            }}
                          >
                            <div className="flex items-center gap-2">
                              {isEditingColumns && (
                                <button
                                  onClick={isHidden ? () => handleShowStatistic(statistic.key) : () => handleHideStatistic(statistic.key)}
                                  className={`transition-all duration-200 p-1 rounded-full active:scale-90 ${
                                    isHidden 
                                      ? 'text-gray-600 dark:text-text-secondary hover:text-highlight-gold hover:bg-highlight-gold/10' 
                                      : 'text-gray-600 dark:text-text-secondary hover:text-market-red hover:bg-market-red/10'
                                  }`}
                                  title={isHidden ? "Show statistic" : "Hide statistic"}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    {isHidden ? (
                                      // Unchecked rounded square (hidden)
                                      <rect x="2" y="2" width="20" height="20" rx="3" ry="3" strokeWidth="2" />
                                    ) : (
                                      // Checked rounded square (visible)
                                      <>
                                        <rect x="2" y="2" width="20" height="20" rx="3" ry="3" strokeWidth="2" fill="white" className="dark:fill-gray-800" />
                                        <path d="M9 12l2 2 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                      </>
                                    )}
                                  </svg>
                                </button>
                              )}
                              <span className={getStatisticLabelSizeClass(fontSize)}>
                                {(() => {
                                  const words = statistic.label.split(' ');
                                  const lastWord = words[words.length - 1];
                                  const otherWords = words.slice(0, -1);
                                  
                                  return (
                                    <>
                                      {otherWords.join(' ')}
                                      {otherWords.length > 0 && ' '}
                                      <span className="whitespace-nowrap">
                                        {lastWord}
                                        {PRO_ONLY_STATISTICS.includes(statistic.key) && (
                                          <span className="ml-1.5 px-1.5 py-0.5 text-xs font-semibold text-gray-900 bg-primary/30 dark:bg-highlight-gold/20 dark:text-highlight-gold rounded">
                                            PRO
                                          </span>
                                        )}
                                      </span>
                                    </>
                                  );
                                })()}
                              </span>
                            </div>
                          </td>
                          {visibleColumns.map((column) => (
                            <td 
                              key={column.daysAgo} 
                              className={`px-1 sm:px-2 md:px-4 lg:px-6 font-semibold ${getTextSizeClass(fontSize)} h-12 sm:h-16 transition-all duration-300 ${statistic.key === 'last_closing_price' && isLastClosingPriceStuck ? 'bg-blue-50 dark:bg-gray-800' : 'bg-surface-light dark:bg-background-card'} ${
                                statistic.key === 'last_closing_price' ? '' : ''
                              } ${isEditingColumns && currentHiddenColumns.includes(column.daysAgo) ? 'opacity-50' : ''}`}
                              style={{ paddingTop: '0px', paddingBottom: '0px' }}
                            >
                              {formatStatisticValue(statistic.key, column.daysAgo)}
                            </td>
                          ))}
                          
                          {/* Empty cell for full screen button column */}
                          <td 
                            className={`px-2 sm:px-4 h-12 sm:h-16 ${statistic.key === 'last_closing_price' && isLastClosingPriceStuck ? 'bg-blue-50 dark:bg-gray-800' : 'bg-surface-light dark:bg-background-card'}`}
                            style={statistic.key === 'last_closing_price' ? { position: 'sticky', right: 0, zIndex: 40 } : undefined}
                          ></td>
                        </tr>
                      </React.Fragment>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
          
          {/* Status Bar */}
          <div className={`px-4 py-2 border-t border-gray-200 dark:border-border-color bg-gray-50 dark:bg-black/10 flex justify-between items-center ${getSmallTextSizeClass(fontSize)} text-gray-600 dark:text-text-secondary rounded-b-md`}>
            <div className="flex items-center gap-4">
              {loading && (
                <div className="flex items-center gap-2">
                  <div className="animate-spin w-3 h-3 border border-highlight-gold border-t-transparent rounded-full"></div>
                  <span>Loading...</span>
                </div>
              )}
              {lastUpdated && !loading && (
                <span>Last updated: {lastUpdated.toLocaleDateString()} {lastUpdated.toLocaleTimeString()}</span>
              )}
            </div>
          </div>
        </div>
      </main>
      
      {/* Full Screen Overlay */}
      {isFullScreen && (
      <div
        className="fixed inset-0 z-[200] flex min-h-0 flex-col bg-background-light dark:bg-background"
        role="dialog"
        aria-modal="true"
      >
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-auto overscroll-contain">
            <table className="min-w-full font-mono" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ 
                  width: `${statisticsColumnWidth}px`, 
                  minWidth: isSmallScreen ? '100px' : '200px', 
                  maxWidth: '500px' 
                }} />
                {visibleColumns.map((_, index) => (
                  <col key={index} style={{ width: '150px' }} />
                ))}
              </colgroup>
              
              {/* Header with sticky positioning */}
              <thead className="sticky-header-border" style={{ position: 'sticky', top: 0, zIndex: 50 }}>
                <tr>
                  {/* Statistics column header */}
                  <th 
                    className={`py-1 px-4 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-10 bg-gray-50 dark:bg-background-card`}
                    style={{ 
                      position: 'sticky', 
                      left: 0, 
                      zIndex: 40
                    }}
                  >
                    <div className="flex items-center">
                      <span>Indicators</span>
                    </div>
                  </th>
                  
                  {/* Date columns headers */}
                  {visibleColumns.map((column) => (
                    <th 
                      key={column.daysAgo} 
                      className={`py-1 px-2 sm:px-4 lg:px-6 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-10 bg-gray-50 dark:bg-background-card`}
                    >
                      <div className="flex flex-col">
                        <div className="flex items-center">
                          <span>{column.label}</span>
                        </div>
                        <span className="text-xs text-gray-500 dark:text-text-secondary font-normal">
                          {dates[column.daysAgo] || <span className="text-gray-400 dark:text-text-secondary">-</span>}
                        </span>
                      </div>
                    </th>
                  ))}
                  
                  {/* Close button - sticky right */}
                  <th 
                    className="py-1 px-4 text-right bg-gray-50 dark:bg-background-card"
                    style={{ position: 'sticky', right: 0, zIndex: 40 }}
                  >
                    <button
                      onClick={() => setIsFullScreen(false)}
                      className="p-1.5 text-gray-500 dark:text-white hover:text-highlight-gold transition-colors duration-200 hover:bg-gray-200 dark:hover:bg-border-color rounded-md"
                      title="Close full screen"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </th>
                </tr>
              </thead>
              
              {/* Table body */}
              <tbody className="bg-surface-light dark:bg-background-card divide-y divide-gray-200 dark:divide-border-color">
                {(() => {
                  // Track which section headers have already been shown to avoid duplicates
                  const shownHeaders = new Set<string>();
                  
                  return visibleStatistics.map((statistic, index) => {
                    const isHidden = currentHiddenStatistics.includes(statistic.key);
                    // Find the original index of this statistic in the full statistics array
                    const originalIndex = TECHNICAL_STATISTICS.findIndex(s => s.key === statistic.key);
                    
                    // Check if we need to render a category header before this statistic
                    const categoryHeader = CATEGORY_HEADERS.find(header => {
                      // Skip if this header has already been shown
                      if (shownHeaders.has(header.label)) {
                        return false;
                      }
                      
                      // Check if this statistic belongs to this section (is at or after startIndex)
                      const belongsToSection = originalIndex >= header.startIndex;
                      
                      // Check if this is the first visible statistic in this section
                      const isFirstVisibleInSection = !TECHNICAL_STATISTICS
                        .slice(header.startIndex, originalIndex)
                        .some(s => !currentHiddenStatistics.includes(s.key));
                      
                      // If this is the first visible statistic in the section, mark the header as shown
                      if (belongsToSection && isFirstVisibleInSection) {
                        shownHeaders.add(header.label);
                      }
                      
                      return belongsToSection && isFirstVisibleInSection;
                    });

                    return (
                      <React.Fragment key={statistic.key}>
                        {/* Category header row */}
                        {categoryHeader && (
                          <tr className="bg-gray-100 dark:bg-border-color">
                            <td 
                              className="px-2 sm:px-4 py-2 text-left font-bold text-gray-800 dark:text-text-primary text-sm bg-gray-100 dark:bg-border-color"
                              style={{ 
                                position: 'sticky', 
                                left: 0, 
                                zIndex: 20,
                                width: `${statisticsColumnWidth}px`,
                                maxWidth: `${statisticsColumnWidth}px`
                              }}
                            >
                              <div 
                                className="whitespace-nowrap"
                                style={{ overflow: 'visible', position: 'relative' }}
                              >
                                <span className="inline">{categoryHeader.label}</span>
                                <button
                                  onClick={() => handleCategoryInfoClick(categoryHeader.label)}
                                  className="inline-flex items-center justify-center w-5 h-5 ml-1 rounded-full text-gray-900 dark:text-text-primary hover:text-primary dark:hover:text-highlight-gold hover:bg-primary/10 dark:hover:bg-highlight-gold/10 transition-colors align-middle"
                                  title={`Learn more about ${categoryHeader.label}`}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                                    <path d="M12 16v-4" strokeWidth="2" strokeLinecap="round"/>
                                    <path d="M12 8h.01" strokeWidth="2" strokeLinecap="round"/>
                                  </svg>
                                </button>
                              </div>
                            </td>
                            {visibleColumns.map((column) => (
                              <td key={column.daysAgo} className={`px-2 sm:px-4 lg:px-6 py-2 text-left font-bold text-gray-800 dark:text-text-primary text-sm bg-gray-100 dark:bg-border-color`}>
                                {/* Empty cell - category name only shown in first column */}
                              </td>
                            ))}
                            
                            {/* Empty cell for close button column */}
                            <td className="px-4 py-2 text-sm font-bold text-gray-800 dark:text-text-primary bg-gray-100 dark:bg-border-color"></td>
                          </tr>
                        )}
                        
                        {/* Statistics row */}
                        <tr 
                          ref={statistic.key === 'last_closing_price' ? lastClosingPriceRowFullScreenRef : null}
                          className={`h-12 sm:h-16 ${statistic.key === 'last_closing_price' ? `sticky top-[39px] z-40 sticky-last-closing-price ${isLastClosingPriceStuckFullScreen ? 'is-stuck' : ''}` : ''}`}
                        >
                          <td 
                            className={`px-2 sm:px-4 text-gray-900 dark:text-text-primary font-medium ${statistic.key === 'last_closing_price' && isLastClosingPriceStuckFullScreen ? 'bg-blue-50 dark:bg-gray-800' : 'bg-surface-light dark:bg-background-card'}`}
                            style={{ 
                              position: 'sticky', 
                              left: 0, 
                              zIndex: 30,
                              width: `${statisticsColumnWidth}px`,
                              maxWidth: `${statisticsColumnWidth}px`,
                              paddingTop: '0px',
                              paddingBottom: '0px',
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word',
                              hyphens: 'auto'
                            }}
                          >
                            <div className="flex items-center gap-2">
                              {isEditingColumns && (
                                <button
                                  onClick={() => isHidden ? handleShowStatistic(statistic.key) : handleHideStatistic(statistic.key)}
                                  className={`transition-all duration-200 p-1 rounded-full active:scale-90 ${
                                    isHidden 
                                      ? 'text-gray-600 dark:text-text-secondary hover:text-highlight-gold hover:bg-highlight-gold/10' 
                                      : 'text-gray-600 dark:text-text-secondary hover:text-market-red hover:bg-market-red/10'
                                  }`}
                                  title={isHidden ? "Show statistic" : "Hide statistic"}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    {isHidden ? (
                                      // Unchecked rounded square (hidden)
                                      <rect x="2" y="2" width="20" height="20" rx="3" ry="3" strokeWidth="2" />
                                    ) : (
                                      // Checked rounded square (visible)
                                      <>
                                        <rect x="2" y="2" width="20" height="20" rx="3" ry="3" strokeWidth="2" fill="white" className="dark:fill-gray-800" />
                                        <path d="M9 12l2 2 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                      </>
                                    )}
                                  </svg>
                                </button>
                              )}
                              <span className={getStatisticLabelSizeClass(fontSize)}>
                                {(() => {
                                  const words = statistic.label.split(' ');
                                  const lastWord = words[words.length - 1];
                                  const otherWords = words.slice(0, -1);
                                  
                                  return (
                                    <>
                                      {otherWords.join(' ')}
                                      {otherWords.length > 0 && ' '}
                                      <span className="whitespace-nowrap">
                                        {lastWord}
                                        {PRO_ONLY_STATISTICS.includes(statistic.key) && (
                                          <span className="ml-1.5 px-1.5 py-0.5 text-xs font-semibold text-gray-900 bg-primary/30 dark:bg-highlight-gold/20 dark:text-highlight-gold rounded">
                                            PRO
                                          </span>
                                        )}
                                      </span>
                                    </>
                                  );
                                })()}
                              </span>
                            </div>
                          </td>
                          {visibleColumns.map((column) => (
                            <td 
                              key={column.daysAgo} 
                              className={`px-1 sm:px-2 md:px-4 lg:px-6 font-semibold ${getTextSizeClass(fontSize)} h-12 sm:h-16 ${statistic.key === 'last_closing_price' && isLastClosingPriceStuckFullScreen ? 'bg-blue-50 dark:bg-gray-800' : 'bg-surface-light dark:bg-background-card'} ${isEditingColumns && currentHiddenColumns.includes(column.daysAgo) ? 'opacity-50' : ''}`}
                              style={{ paddingTop: '0px', paddingBottom: '0px' }}
                            >
                              {formatStatisticValue(statistic.key, column.daysAgo)}
                            </td>
                          ))}
                          
                          {/* Empty cell for full screen button column */}
                          <td 
                            className={`px-2 sm:px-4 h-12 sm:h-16 ${statistic.key === 'last_closing_price' && isLastClosingPriceStuckFullScreen ? 'bg-blue-50 dark:bg-gray-800' : 'bg-surface-light dark:bg-background-card'}`}
                            style={statistic.key === 'last_closing_price' ? { position: 'sticky', right: 0, zIndex: 40 } : undefined}
                          ></td>
                        </tr>
                      </React.Fragment>
                    );
                  });
                })()}
              </tbody>
            </table>
        </div>
          
          {/* Status Bar */}
          <div className={`flex-shrink-0 px-4 py-2 border-t border-gray-200 dark:border-border-color bg-gray-50 dark:bg-black/10 flex justify-between items-center ${getSmallTextSizeClass(fontSize)} text-gray-600 dark:text-text-secondary rounded-b-md`}>
            <div className="flex items-center gap-4">
              {loading && (
                <div className="flex items-center gap-2">
                  <div className="animate-spin w-3 h-3 border border-highlight-gold border-t-transparent rounded-full"></div>
                  <span>Loading...</span>
                </div>
              )}
              {lastUpdated && !loading && (
                <span>Last updated: {lastUpdated.toLocaleDateString()} {lastUpdated.toLocaleTimeString()}</span>
              )}
            </div>
          </div>
      </div>
      )}
      
      {/* Footer */}
      <div className="mt-4 sm:mt-8">
        <Footer />
      </div>
      </div>
      
      {/* Category Info Modal */}
      <CategoryInfoModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        categoryName={selectedCategory}
      />
    </>
  );
};

export default TickerTechnicals;
